import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useFarmGroup } from 'modules/member/api/selector'
import { useGetCRUDByFeature } from 'modules/user/api/selector'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { Category } from 'modules/inventory/api/type'
import { useMutateRemoveProduct } from 'modules/inventory/api/mutation'
import { alpha, useTheme } from '@mui/material'
import CreateProductDialog from 'modules/inventory/pages/product/CreateProductPage'

interface Props {
  productId: string
  onClose: () => void
  open: boolean
  productItem: Category
  anchorEl: HTMLElement | null
  pageSize: number
}

const ProductTools = ({ productId, onClose, open, productItem, anchorEl, pageSize }: Props) => {
  const { t } = useTranslation('product')
  const cruds = useGetCRUDByFeature(FeatureKey.Inventory)
  const { refetch: refetchFarmGroup } = useFarmGroup('', { enabled: false })
  const theme = useTheme();

  const removeProductMutation = useMutateRemoveProduct(pageSize)

  const [openEditPage, setOpenEditPage] = useState(false)
  const [editingProductId, setEditingProductId] = useState<string | undefined>(undefined)

  const handleCloseEditDialog = () => {
    setOpenEditPage(false)
    setEditingProductId(undefined)
  }

  const { dialog, openDialog: openConfirmDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (productId) {
        await removeProductMutation
          .mutateAsync(productId, {
            onSuccess: () => {
              onClose()
            },
          })
          .finally(() => {
          })
      }
    },
  })

  const handleEditProduct = () => {
    setEditingProductId(productId)
    setOpenEditPage(true)
    onClose()
  }

  const toolsMenuItems = [
    {
      icon: <img src="/static/icons/ci_edit.svg" alt="diary" />,
      title: t('product.edit'),
      crudIdx: CRUDAction.Update,
      onClick: handleEditProduct,
    },
    {
      icon: <img src="/static/icons/ci_delete.svg" alt="diary" />,
      title: t('product.delete'),
      crudIdx: CRUDAction.Delete,
      onClick: openConfirmDeleteDialog,
    },
  ]

  const getTools = () => {
    if (!cruds) {
      return []
    }
    const tools = toolsMenuItems.filter(
      (m) =>
        m.crudIdx === undefined ||
        cruds[m.crudIdx] === 1
    )

    return tools
  }

  return (
    <Paper>
      <Menu
        id={`edit-card-${productId}`}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ 'aria-labelledby': `basic-button-${productId}` }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{ elevation: 2, sx: { borderRadius: 2, minWidth: 160 } }}
      >
        <MenuList sx={{ py: 0 }}>
          {getTools().map((menuItem, index) => (
            <MenuItem key={index} onClick={menuItem.onClick}>
              <ListItemText>
                <Typography variant="subtitle1">{menuItem.title}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {dialog}
      <CreateProductDialog
        open={openEditPage}
        onClose={handleCloseEditDialog}
        productId={editingProductId}
      />
    </Paper>
  )
}

export default ProductTools
